import { useEffect, useState, type FormEvent } from "react";
import { Button, Input, Label, Select, Textarea } from "@/components/ui/form";
import { Card, CardBody } from "@/components/ui/card";
import { apiFetch } from "@/utils/api";

interface Customer {
  id: string;
  company_name: string;
  contacts: { id: string; name: string }[];
}

export function RfqEditSection({
  rfqId,
  rfq,
  onSaved,
  onCancel,
}: {
  rfqId: string;
  rfq: Record<string, unknown>;
  onSaved: () => Promise<void>;
  onCancel: () => void;
}) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerId, setCustomerId] = useState(String(rfq.customer_id));
  const [contactId, setContactId] = useState(String(rfq.customer_contact_id ?? ""));
  const [deliveryDate, setDeliveryDate] = useState(String(rfq.requested_delivery_date ?? ""));
  const [notes, setNotes] = useState(String(rfq.notes ?? ""));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    apiFetch<{ customers: Customer[] }>("/customers").then((d) => setCustomers(d.customers));
  }, []);

  const selectedCustomer = customers.find((c) => c.id === customerId);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await apiFetch(`/rfqs/${rfqId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerId,
          customerContactId: contactId || null,
          requestedDeliveryDate: deliveryDate || null,
          notes,
        }),
      });
      await onSaved();
      onCancel();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
      setSaving(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardBody>
        <form onSubmit={save} className="space-y-4 max-w-2xl">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Customer</Label>
              <Select value={customerId} onChange={(e) => { setCustomerId(e.target.value); setContactId(""); }} required>
                <option value="">Select customer</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.company_name}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Requested By</Label>
              <Select value={contactId} onChange={(e) => setContactId(e.target.value)}>
                <option value="">Select contact</option>
                {(selectedCustomer?.contacts ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Delivery Date</Label>
              <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          </div>
          {error && <p className="text-sm text-argon-danger">{error}</p>}
          <div className="flex gap-2">
            <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save RFQ"}</Button>
            <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}
